import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { WhatsappDisplayNameStatus } from "@jugl-web/rest-api/integrations/models/common-types/WhatsappDisplayNameStatus";
import {
  AvatarSelect,
  Button,
  DataLoadingWrapper,
  InteractiveContainer,
  PlainButton,
  TextInput,
  Tooltip,
} from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import {
  isAPIError,
  isWhatsappApiError,
  useFormFieldsValidators,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { CRMAccountCard } from "../../../../components/CRMAccountCard";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { CRMStatusPill } from "../../../../components/CRMStatusPill";
import { WhatsappPinDialog } from "../WhatsappPinDialog";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as HelpIcon } from "./assets/help.svg";
import navigationImage from "./assets/navigation.png";
import { ReactComponent as ProfileIcon } from "./assets/profile.svg";
import { ReactComponent as SearchIcon } from "./assets/search.svg";
import { ReactComponent as ShareIcon } from "./assets/share.svg";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import whatsappBg from "./assets/whatsapp-bg.webp";
import { DecorationTile } from "./components/DecorationTile";
import { PreviewRow } from "./components/PreviewRow";

interface WhatsappProfileInfo {
  img: File;
  description: string;
  email: string;
  websites: { value: string }[];
}

const MAX_DESCRIPTION_LENGTH = 512;
const MAX_EMAIL_LENGTH = 128;
const MAX_WEBSITE_LENGTH = 256;
const MAX_WEBSITE_COUNT = 2;

const TooltipIconButton = ({
  icon,
  tooltip,
}: {
  icon: React.ReactNode;
  tooltip: React.ReactNode;
}) => {
  return (
    <Tooltip
      renderTrigger={({ props, ref }) => (
        <PlainButton
          className="bg-grey-100 hover:bg-grey-200 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
          ref={ref}
          {...props}
        >
          {icon}
        </PlainButton>
      )}
    >
      {tooltip}
    </Tooltip>
  );
};
export const WhatsappProfileTab: React.FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { whatsappAgent } = useWhatsappAccount();

  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
  const [isPinInvalid, setIsPinInvalid] = useState(false);

  const [updateWhatsappProfile, { isLoading: isUpdateWhatsappProfileLoading }] =
    integrationsApi.useUpdateWhatsappProfileMutation();
  const [
    updateWhatsappDisplayName,
    { isLoading: isUpdateWhatsappDisplayNameLoading },
  ] = integrationsApi.useUpdateWhatsappDisplayNameMutation();
  const [registerWhatsappAction] =
    integrationsApi.useRegisterWhatsappActionMutation();
  const { toast } = useToast();
  const { registerDirtySource } = useNavigationGuardContext();
  const {
    data: whatsappProfile,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = integrationsApi.useGetWhatsappProfileQuery({ entityId });

  const { emailValidator, lengthValidator } = useFormFieldsValidators();

  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields, errors },
    reset,
    watch,
  } = useForm<WhatsappProfileInfo>({
    mode: "onChange",
    defaultValues: {
      img: undefined,
      description: "",
      email: "",
      websites: [{ value: "" }],
    },
  });

  const [name, setName] = useState("");
  const { description, email, img, websites: profileWebsites } = watch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<ThunkDispatch<any, unknown, AnyAction>>();

  const profileImageSrc = img
    ? URL.createObjectURL(img)
    : whatsappProfile?.profile_picture_url;

  useEffect(() => {
    if (whatsappAgent) {
      setName(whatsappAgent.name);
    }
  }, [whatsappAgent]);

  const {
    fields: websites,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "websites",
  });

  useEffect(() => {
    if (whatsappProfile) {
      const mappedWebsites = whatsappProfile.websites?.map((item) => ({
        value: item,
      })) || [{ value: "" }];

      reset({
        img: undefined,
        description: whatsappProfile.description || "",
        email: whatsappProfile.email || "",
        websites: mappedWebsites,
      });

      replace(mappedWebsites);
    }
  }, [whatsappProfile, reset, replace]);

  const displayErrorToast = useCallback(
    () =>
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      ),
    [t, toast]
  );

  const handleUpdateInfo = useCallback(
    async (data: WhatsappProfileInfo) => {
      const changedValues: Partial<WhatsappProfileInfo> = {};

      (Object.keys(dirtyFields) as (keyof WhatsappProfileInfo)[]).forEach(
        (key) => {
          if (!dirtyFields[key]) return;

          const value = data[key];

          if (key === "img" && value instanceof File) {
            changedValues.img = value;
          } else if (
            (key === "description" || key === "email") &&
            typeof value === "string"
          ) {
            changedValues[key] = value;
          } else if (key === "websites" && Array.isArray(value)) {
            changedValues.websites = value;
          }
        }
      );

      const {
        img: profileImg,
        websites: whatsappWebsites,
        ...profileFields
      } = changedValues;

      const resp = await updateWhatsappProfile({
        entityId,
        img: changedValues.img,
        profile: {
          ...profileFields,
          websites: whatsappWebsites?.map((item) => item.value).filter(Boolean),
        },
      });

      if (resp && "data" in resp) {
        toast(
          t({
            id: "client-support-page.whatsapp-profile-updated",
            defaultMessage: "WhatsApp profile info was updated",
          })
        );
        return;
      }

      if (
        resp &&
        "error" in resp &&
        isWhatsappApiError(resp.error) &&
        "error" in resp.error.data
      ) {
        if (resp.error.data.error.error_subcode === 3441013) {
          toast(
            t({
              id: "client-support-page.image-must-be-larger",
              defaultMessage: "Image size must be at least 192x192 pixels",
            }),
            { variant: "error" }
          );
          return;
        }
      }

      displayErrorToast();
    },
    [dirtyFields, updateWhatsappProfile, entityId, displayErrorToast, toast, t]
  );

  const handleUpdateDisplayName = useCallback(async () => {
    const resp = await updateWhatsappDisplayName({ entityId, name });

    if (resp && "data" in resp) {
      setIsPinDialogOpen(true);
      return;
    }

    if (
      resp &&
      "error" in resp &&
      isAPIError(resp.error) &&
      resp.error.data.errors === "Display Name Change Request Limit Exceeded"
    ) {
      toast(
        t({
          id: "client-support-page.display-name-change-request-limit-exceeded",
          defaultMessage: "Display Name change request limit exceeded",
        }),
        { variant: "error" }
      );
      return;
    }

    displayErrorToast();
  }, [displayErrorToast, entityId, name, t, toast, updateWhatsappDisplayName]);

  const handleRegisterWhatsappAction = useCallback(
    async (pin: string) => {
      setIsPinInvalid(false);

      const resp = await registerWhatsappAction({
        entityId,
        pin,
      });

      if (resp && "data" in resp) {
        setIsPinDialogOpen(false);
        dispatch(
          integrationsApi.util.updateQueryData(
            "getCRMConfig",
            {
              entityId,
            },
            (crmConfigDraft) => {
              if (crmConfigDraft.whatsapp?.phone_number) {
                crmConfigDraft.whatsapp.phone_number.name_status =
                  WhatsappDisplayNameStatus.PENDING_REVIEW;
              }
            }
          )
        );
        toast(
          t({
            id: "client-support-page.whatsapp-display-name-updated",
            defaultMessage: "WhatsApp display name was updated",
          })
        );
        return;
      }

      if (
        resp &&
        "error" in resp &&
        isWhatsappApiError(resp.error) &&
        "error" in resp.error.data &&
        resp.error.data.error.code === 133005
      ) {
        setIsPinInvalid(true);
        return;
      }

      displayErrorToast();
    },
    [
      dispatch,
      displayErrorToast,
      entityId,
      integrationsApi.util,
      registerWhatsappAction,
      t,
      toast,
    ]
  );

  useEffect(() => {
    return registerDirtySource("whatsapp-profile-tab", isDirty);
  }, [isDirty, registerDirtySource]);

  return (
    <DataLoadingWrapper
      isError={isError}
      isLoading={isLoading || isFetching}
      onRetry={refetch}
    >
      <div className="flex items-start gap-8">
        <div className="flex grow flex-col gap-8">
          <CRMAccountCard
            icon={ProfileIcon}
            title={t({
              id: "client-support-page.wa-profile-appearance",
              defaultMessage: "WA Profile Appearance",
            })}
            description={t({
              id: "client-support-page.information-will-be-displayed-for-clients",
              defaultMessage:
                "This information will be displayed for your Clients",
            })}
          />
          <CRMInfoSection
            title={t({
              id: "client-support-page.display-name",
              defaultMessage: "Display Name:",
            })}
          >
            <TextInput
              classNames={{
                wrapperClassName: "w-full",
                endContentWrapperClassName:
                  whatsappAgent?.phone_number.name_status ===
                  WhatsappDisplayNameStatus.PENDING_REVIEW
                    ? "bg-[rgba(239,239,239,0.302)]"
                    : "bg-white",
              }}
              label={t({
                id: "client-support-page.display-name",
                defaultMessage: "Display Name",
              })}
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              isDisabled={
                whatsappAgent?.phone_number.name_status ===
                WhatsappDisplayNameStatus.PENDING_REVIEW
              }
              value={name}
              onChange={(e) => setName(e.currentTarget.value.trimStart())}
              endContent={(() => {
                const isNameApproved =
                  whatsappAgent?.phone_number.name_status ===
                  WhatsappDisplayNameStatus.APPROVED;
                const isNamePending =
                  whatsappAgent?.phone_number.name_status ===
                  WhatsappDisplayNameStatus.PENDING_REVIEW;
                const isNameDeclined =
                  whatsappAgent?.phone_number.name_status ===
                  WhatsappDisplayNameStatus.DECLINED;
                const isNamePendingOrDeclined = isNamePending || isNameDeclined;
                const isNameDifferent = name !== whatsappAgent?.name;

                if (isNameApproved && !isNameDifferent) {
                  return (
                    <div className="flex gap-2.5">
                      <CRMStatusPill status="approved" />
                      <TooltipIconButton
                        icon={<HelpIcon />}
                        tooltip={t({
                          id: "client-support-page.display-name-is-approved",
                          defaultMessage:
                            "The name had been approved ✅ You can download your certificate now ",
                        })}
                      />
                    </div>
                  );
                }

                if (isNamePendingOrDeclined || isNameDifferent) {
                  return (
                    <>
                      {isNamePendingOrDeclined && (
                        <>
                          <div className="flex items-center gap-2.5">
                            <CRMStatusPill
                              status={
                                isNamePending ? "pendingReview" : "declined"
                              }
                            />
                            <Tooltip
                              renderTrigger={({ props, ref }) => (
                                <PlainButton
                                  className="bg-grey-100 hover:bg-grey-200 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
                                  ref={ref}
                                  {...props}
                                >
                                  <HelpIcon />
                                </PlainButton>
                              )}
                            >
                              {isNamePending
                                ? t(
                                    {
                                      id: "client-support-page.display-name-is-being-verified",
                                      defaultMessage:
                                        "It will take some time for Meta team to verify display name. You will get notification once verification will be completed ℹ️{br}{br}Currently your Account is still using previous name. Once new display name will be verified, it will be automatically updated 🤓",
                                    },
                                    { br: <br /> }
                                  )
                                : t({
                                    id: "client-support-page.display-name-was-not-approved",
                                    defaultMessage:
                                      "Account Display Name was not approved as it doesn't meet Meta guidelines ⚠️ Please update it and try again",
                                  })}
                            </Tooltip>
                          </div>
                          {isNameDifferent && (
                            <div className="bg-dark-100 mx-4 h-10 w-px" />
                          )}
                        </>
                      )}
                      {isNameDifferent && (
                        <Button
                          className="h-10 px-8"
                          onClick={handleUpdateDisplayName}
                          isDisabled={isUpdateWhatsappDisplayNameLoading}
                        >
                          {t({
                            id: "client-support-page.verify-now",
                            defaultMessage: "Verify now",
                          })}
                        </Button>
                      )}
                    </>
                  );
                }

                return null;
              })()}
            />
          </CRMInfoSection>
          <CRMInfoSection
            title={t({
              id: "client-support-page.additional-info",
              defaultMessage: "Additional Info:",
            })}
          >
            <div className="flex items-center gap-6">
              <Controller
                control={control}
                name="img"
                render={({ field: { value, onChange } }) => (
                  <AvatarSelect
                    onSave={onChange}
                    defaultSrc={whatsappProfile?.profile_picture_url}
                    value={value}
                  />
                )}
              />
              <div className="flex flex-col gap-4">
                <span className="text-dark-800 font-secondary text-sm font-medium">
                  {t({
                    id: "client-support-page.profile-image",
                    defaultMessage: "Profile Image",
                  })}
                </span>
                <span className="text-dark-700 font-secondary text-sm">
                  {t({
                    id: "client-support-page.profile-image-tip",
                    defaultMessage:
                      "Better to upload square image greater than 192×192px",
                  })}
                </span>
              </div>
            </div>
            <Controller
              control={control}
              name="description"
              rules={{
                ...lengthValidator(MAX_DESCRIPTION_LENGTH),
              }}
              render={({ field: { onChange, ...props } }) => (
                <TextAreaInput
                  classNames={{ wrapperClassName: "w-full" }}
                  label={t({
                    id: "common.description",
                    defaultMessage: "Description",
                  })}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  onChange={(e) => onChange(e.target.value.trimStart())}
                  {...props}
                  isInvalid={!!errors.description}
                  errorMessage={errors.description?.message}
                  lengthIndicator={{
                    max: MAX_DESCRIPTION_LENGTH,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="email"
              rules={{
                ...lengthValidator(MAX_EMAIL_LENGTH),
                ...emailValidator(),
              }}
              render={({ field: { onChange, ...props } }) => (
                <TextInput
                  classNames={{ wrapperClassName: "w-full" }}
                  label={t({
                    id: "common.email",
                    defaultMessage: "Email",
                  })}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  onChange={(e) => onChange(e.target.value.trimStart())}
                  {...props}
                  isInvalid={!!errors.email}
                  errorMessage={errors.email?.message}
                  lengthIndicator={{
                    max: MAX_EMAIL_LENGTH,
                  }}
                />
              )}
            />
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-6">
                {websites.map((item, idx) => (
                  <Controller
                    control={control}
                    name={`websites.${idx}.value`}
                    rules={{
                      ...lengthValidator(MAX_WEBSITE_LENGTH),
                      pattern: {
                        value: /^https?:\/\/[^\s<>`"'|)]+$/i,
                        message: t({
                          id: "client-support-page.enter-valid-url",
                          defaultMessage: "Enter a valid URL address",
                        }),
                      },
                    }}
                    render={({ field: { onChange, ...props } }) => (
                      <TextInput
                        key={item.id}
                        classNames={{ wrapperClassName: "w-full" }}
                        label={`${t({
                          id: "common.website",
                          defaultMessage: "Website",
                        })}${idx ? ` ${idx + 1}` : ""}`}
                        placeholder={t({
                          id: "common.enter",
                          defaultMessage: "Enter",
                        })}
                        endContent={
                          websites.length > 1 ? (
                            <PlainButton onClick={() => remove(idx)}>
                              <TrashIcon />
                            </PlainButton>
                          ) : undefined
                        }
                        onChange={(e) => onChange(e.target.value.trimStart())}
                        {...props}
                        isInvalid={!!errors.websites?.[idx]}
                        errorMessage={errors.websites?.[idx]?.value?.message}
                        lengthIndicator={{
                          max: MAX_WEBSITE_LENGTH,
                        }}
                      />
                    )}
                  />
                ))}
              </div>
              {websites.length < MAX_WEBSITE_COUNT && (
                <InteractiveContainer
                  onClick={() => append({ value: "" })}
                  className="bg-grey-100 hover:bg-grey-200 flex h-8 w-max items-center gap-1 rounded px-2 transition-colors"
                >
                  <span className="text-primary-800 font-secondary text-xs">
                    {t({
                      id: "client-support-page.add-another-website",
                      defaultMessage: "Add another website",
                    })}
                  </span>
                  <AddIcon />
                </InteractiveContainer>
              )}
            </div>
            <Button
              className="w-[200px]"
              isDisabled={!isDirty || isUpdateWhatsappProfileLoading}
              onClick={handleSubmit(handleUpdateInfo)}
            >
              {t({
                id: "common.save-changes",
                defaultMessage: "Save changes",
              })}
            </Button>
          </CRMInfoSection>
        </div>
        <div className="border-dark-900 flex min-h-[753px] w-[375px] shrink-0 flex-col overflow-hidden rounded-[20px] border border-solid">
          <img
            src={navigationImage}
            className="h-full w-full shrink-0"
            alt="Navigation bar"
          />
          <div
            className="flex grow flex-col items-center gap-6 bg-[#ece6de] bg-cover bg-center p-6 text-center"
            style={{ backgroundImage: `url(${whatsappBg})` }}
          >
            <div className="border-grey-100 bg-grey-100 flex h-[118px] w-[118px] items-center justify-center overflow-hidden rounded-full border-2 border-solid">
              {profileImageSrc ? (
                <img
                  src={profileImageSrc}
                  alt=""
                  className="h-full w-full object-cover object-center"
                />
              ) : (
                <UserIcon />
              )}
            </div>
            <div className="flex w-full flex-col gap-2 overflow-hidden">
              <span className="text-dark font-secondary truncate text-2xl font-semibold leading-7">
                {whatsappAgent?.name}
              </span>
              <span className="text-dark-800 font-secondary truncate leading-[140%] tracking-[0.16px]">
                {whatsappAgent?.phone_number.display_phone_number}
              </span>
            </div>
            <div className="flex w-full flex-col gap-4">
              <div className="flex gap-4">
                <DecorationTile
                  icon={<ShareIcon />}
                  label={t({
                    id: "common.share",
                    defaultMessage: "Share",
                  })}
                />
                <DecorationTile
                  icon={<SearchIcon />}
                  label={t({
                    id: "common.search",
                    defaultMessage: "Search",
                  })}
                />
              </div>
            </div>
            <div className="flex w-full flex-col rounded-lg bg-white/90 p-4 text-left">
              <PreviewRow
                label={t({
                  id: "common.description",
                  defaultMessage: "Description",
                })}
                value={description}
              />
              <PreviewRow
                label={t({
                  id: "common.email",
                  defaultMessage: "Email",
                })}
                value={email}
              />
              <PreviewRow
                label={t({
                  id: "common.website",
                  defaultMessage: "Website",
                })}
                value={profileWebsites
                  .map((item) => item.value)
                  .filter(Boolean)
                  .join(", ")}
                hideDivider
              />
            </div>
          </div>
        </div>
      </div>
      <WhatsappPinDialog
        isOpen={isPinDialogOpen}
        onRequestClose={() => {
          setIsPinDialogOpen(false);
          setIsPinInvalid(false);
        }}
        verifyActionCallback={handleRegisterWhatsappAction}
        initialScreen="confirmAction"
        isPinInvalid={isPinInvalid}
      />
    </DataLoadingWrapper>
  );
};
